import { useMutation, useQueryClient } from "@tanstack/react-query";

interface UseGachaPrizePayload {
  userPrizeId: number;
  appId: string;
  accessToken: string;
}

/**
 * Hook to mark a gacha prize as used
 */
export function useUseGachaPrize() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UseGachaPrizePayload) => {
      const { userPrizeId, appId, accessToken } = payload;
      if (!appId) throw new Error("App ID is required");
      if (!accessToken) throw new Error("Access token is required");
      if (!userPrizeId) throw new Error("User Prize ID is required");

      const gachaApiUrl = process.env.NEXT_PUBLIC_GACHA_API_URL;

      if (!gachaApiUrl) {
        throw new Error("NEXT_PUBLIC_GACHA_API_URL is not configured");
      }

      // Encode app_id: BASE64 then URL encode
      //const base64EncodedAppId = Buffer.from(appId).toString("base64");
      const urlEncodedAppId = encodeURIComponent(
        "OdvHehHjImxxoWWasHG4mUfsZZ/mZUYpUnKHPAgc/Ik=",
      );

      const response = await fetch(
        `${gachaApiUrl}api/app/welcia/user/prize/${userPrizeId}/update/used`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "Ocp-Apim-Subscription-Key":
              process.env.NEXT_PUBLIC_SUBSCRIPTION_KEY || "",
            Authorization: `Bearer ${accessToken}`,
          },
          body: `app_id=${urlEncodedAppId}`,
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to update gacha prize as used: ${response.statusText}`,
        );
      }

      return await response.json();
    },
    onSuccess: async (_data, variables) => {
      // Invalidate and refetch gacha offers after updating
      await queryClient.invalidateQueries({
        queryKey: ["gachaOffers", variables.appId],
      });
    },
  });
}
